import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { SidebarModule } from 'primeng/sidebar';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { constants } from '../../constants';
import { NumericInputDirective } from '../../directives/numeric-input.directive';
import { EcommerceCategory } from '../../models';
import { CurrencyFormatterPipe } from '../../pipes/currency-formatter.pipe';
import { SearchFilter } from '../../services/search/search.service';
import { areEqualArrays } from '../../utils/array.utils';
import { getEcommerceCategoryLabelById } from '../../utils/ecommerce.utils';
import { areEqualObjects } from '../../utils/object.utils';
import { ButtonComponent } from '../button/button.component';

interface FiltersSideBar {
  category: string | null;
  discountPercentage: number | null;
  discounts: number[];
  freeShipping: boolean;
  max: number | null;
  min: number | null;
  sortBy: SortValue | null;
}

export interface FilterSidebarValues {
  category: string | null;
  encodedFilters: string | null;
  encodedOrder: string | null;
}

export interface SortValue {
  direction: 'asc' | 'desc';
  field: string;
}

@Component({
  selector: 'app-ecommerce-filter-sidebar',
  standalone: true,
  imports: [
    AccordionModule,
    ButtonComponent,
    CommonModule,
    CurrencyFormatterPipe,
    FormsModule,
    NumericInputDirective,
    ReactiveFormsModule,
    SidebarModule,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './ecommerce-filter-sidebar.component.html',
  styleUrl: './ecommerce-filter-sidebar.component.scss',
})
export class EcommerceFilterSidebarComponent implements OnChanges, OnDestroy {
  @Input() category: string | null = null;
  @Input() categories: EcommerceCategory[] = [];
  @Input() currency = constants.defaultCurrency;
  @Input() filtersQueryParams: string | null;
  @Input() locale = constants.defaultLocale;
  @Input() orderQueryParams: string | null;
  @Output() changeFilters = new EventEmitter<FilterSidebarValues>();
  @Output() hasActiveFilters = new EventEmitter<boolean>();
  protected changedFilters = false;
  protected defaultFilters = true;
  protected discountOptions = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7];
  protected form: FormGroup<{
    category: FormControl<string | null>;
    discounts: FormControl<number[] | null>;
    freeShipping: FormControl<boolean | null>;
    max: FormControl<number | null>;
    min: FormControl<number | null>;
    sortBy: FormControl<SortValue | null>;
  }>;
  protected isMobile = false;
  protected isVisible = false;
  protected sortOptionDefault: SortValue;
  protected sortOptions: { label: string; value: SortValue; }[] = [
    { label: 'Mejor coincidencia', value: { field: 'default', direction: 'desc' } },
    { label: 'Precio de mayor a menor', value: { field: 'price', direction: 'desc' } },
    { label: 'Precio de menor a mayor', value: { field: 'price', direction: 'asc' } },
  ];
  protected categoriesOptions: { label: string; value: string; }[] = [];
  protected categoryLabels: Record<string, string> = {};
  private filtersApplied: FiltersSideBar;
  private viewDestroyed = new Subject<void>();

  constructor() {
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filtersQueryParams'] || changes['orderQueryParams'] || changes['category']) {
      const { discounts, freeShipping, max, min, sortBy } = this.decodeFilterValues();
      this.form.patchValue({
        category: this.category,
        discounts,
        freeShipping,
        max,
        min,
        sortBy: this.sortOptions.find((option) => areEqualObjects(option.value, sortBy))?.value ?? this.sortOptionDefault,
      }, { emitEvent: false });
      this.filtersApplied = this.getActiveFilters();
      this.form.updateValueAndValidity();
      this.validFilterChanges();
      this.hasActiveFilters.emit(!this.defaultFilters);
    }
    if (changes['categories']) {
      this.categoriesOptions = this.categories.map(({ id, label }) => ({ label, value: id }));
      this.categoryLabels = getEcommerceCategoryLabelById(this.categories);
    }
  }

  ngOnDestroy(): void {
    this.viewDestroyed.next();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 480;
  }

  close(): void {
    this.isVisible = false;
  }

  deleteFilters(): void {
    this.form.patchValue({
      category: null,
      discounts: [],
      freeShipping: false,
      max: null,
      min: null,
      sortBy: this.sortOptionDefault,
    }, { emitEvent: false });
    this.form.updateValueAndValidity();
    this.filtersApplied = this.getActiveFilters();
    this.validFilterChanges();
    this.changeFilters.emit({ category: null, encodedFilters: null, encodedOrder: null });
    this.hasActiveFilters.emit(!this.defaultFilters);
  }

  open(): void {
    this.isVisible = true;
  }

  protected cancel(): void {
    this.close();
    const { category, discounts, freeShipping, max, min, sortBy } = this.filtersApplied;
    this.form.patchValue({
      category,
      discounts,
      freeShipping,
      max,
      min,
      sortBy: this.sortOptions.find((option) => areEqualObjects(option.value, sortBy))?.value,
    }, { emitEvent: false });
    this.form.updateValueAndValidity();
    this.validFilterChanges();
  }

  protected filter(): void {
    this.filtersApplied = this.getActiveFilters();
    this.changeFilters.emit(this.getFilterSidebarValues());
    this.hasActiveFilters.emit(!this.defaultFilters);
    this.close();
    this.changedFilters = false;
  }

  protected deleteFiltersAndClose(): void {
    this.close();
    this.deleteFilters();
  }

  protected deleteValue<T>(control: FormControl<T[] | null>, value: T): void {
    const values = control.value;
    const valuesFiltered = values ? values.filter((v) => v !== value) : [];
    control.patchValue(valuesFiltered);
  }

  protected getSortByLabel(sortValue: SortValue | null): string {
    return this.sortOptions.find(({ value }) => value.field === sortValue?.field && value.direction === sortValue?.direction)?.label ?? '';
  }

  protected resetControls<T>(controls: FormControl | FormControl[], value?: T): void {
    if (Array.isArray(controls)) {
      controls.forEach((control) => control.patchValue(value ?? null));
    } else {
      controls.patchValue(value ?? null);
    }
  }

  protected onDiscountToggle(event: Event, discount: number): void {
    const control = this.form.controls.discounts;
    const discounts = control.value ?? [];
    const checked = (event.target as HTMLInputElement).checked;
    control.setValue(checked ? [...discounts, discount] : discounts.filter((value) => value !== discount));
    control.markAsDirty();
    control.markAsTouched();
  }

  private decodeFilterValues(): FiltersSideBar {
    const filters: SearchFilter[] | null = this.filtersQueryParams ? JSON.parse(this.filtersQueryParams) : null;
    const order: SortValue | null = this.orderQueryParams ? JSON.parse(this.orderQueryParams) : null;
    const decodedFilters: Omit<FiltersSideBar, 'discounts'> = {
      category: this.category,
      discountPercentage: filters?.find((f) => f.field === 'discountPercentage')?.value as number ?? null,
      freeShipping: !!filters?.find((f) => f.field === 'tags' && f.operator === 'in' && f.value === 'free-shipping'),
      max: filters?.find((f) => f.field === 'price' && f.operator === '<=')?.value as number ?? null,
      min: filters?.find((f) => f.field === 'price' && f.operator === '>=')?.value as number ?? null,
      sortBy: order,
    };
    return { ...decodedFilters, discounts: decodedFilters.discountPercentage ? [decodedFilters.discountPercentage] : [] };
  }

  private getActiveFilters(): FiltersSideBar {
    const { category, discounts, freeShipping, max, min, sortBy } = this.form.getRawValue();
    const discountPercentage = discounts?.length ? Math.min(...discounts) : null;
    return { category, discountPercentage, discounts: discounts ?? [], freeShipping: !!freeShipping, max, min, sortBy };
  }

  private getFilterSidebarValues(): FilterSidebarValues {
    const { category, discountPercentage, freeShipping, max, min, sortBy } = this.getActiveFilters();
    const searchFilters: SearchFilter[] = [];
    if (discountPercentage) {
      searchFilters.push({ field: 'discountPercentage', operator: '>=', value: discountPercentage });
    }
    if (max) {
      searchFilters.push({ field: 'price', operator: '<=', value: max });
    }
    if (min) {
      searchFilters.push({ field: 'price', operator: '>=', value: min });
    }
    if (freeShipping) {
      searchFilters.push({ field: 'tags', operator: 'in', value: 'free-shipping' });
    }
    const encodedFilters = searchFilters.length ? JSON.stringify(searchFilters) : null;
    const encodedOrder = sortBy?.field !== 'default' ? JSON.stringify(sortBy) : null;
    return { category, encodedFilters, encodedOrder };
  }

  private initialize(): void {
    this.sortOptionDefault = this.sortOptions[0].value;
    this.form = new FormGroup({
      category: new FormControl<string | null>(null),
      discounts: new FormControl<number[] | null>([]),
      freeShipping: new FormControl<boolean | null>(false),
      max: new FormControl<number | null>(null, [Validators.min(0)]),
      min: new FormControl<number | null>(null, [Validators.min(0)]),
      sortBy: new FormControl<SortValue | null>(this.sortOptionDefault),
    });
    this.filtersApplied = this.getActiveFilters();
    this.form.controls.min.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.viewDestroyed),
      ).subscribe({
        next: (minPrice) => {
          this.form.controls.max.setValidators([Validators.min(minPrice ?? 0)]);
          this.form.controls.max.updateValueAndValidity();
          this.validFilterChanges();
        },
      });
    this.form.controls.max.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.viewDestroyed),
      ).subscribe({
        next: (maxPrice) => {
          if (maxPrice) {
            this.form.controls.min.setValidators([Validators.min(0), Validators.max(maxPrice)]);
          } else {
            this.form.controls.min.clearValidators();
            this.form.controls.min.setValidators([Validators.min(0)]);
          }
          this.form.controls.min.updateValueAndValidity();
          this.validFilterChanges();
        },
      });
    this.form.controls.discounts.valueChanges.pipe(takeUntil(this.viewDestroyed)).subscribe({
      next: () => this.validFilterChanges(),
    });
    this.form.controls.sortBy.valueChanges.pipe(takeUntil(this.viewDestroyed)).subscribe({
      next: () => this.validFilterChanges(),
    });
    this.form.controls.category.valueChanges.pipe(takeUntil(this.viewDestroyed)).subscribe({
      next: () => this.validFilterChanges(),
    });
    this.form.controls.freeShipping.valueChanges.pipe(takeUntil(this.viewDestroyed)).subscribe({
      next: () => this.validFilterChanges(),
    });
  }

  private validFilterChanges(): void {
    const { category, discountPercentage, freeShipping, discounts, max, min, sortBy } = this.getActiveFilters();
    this.defaultFilters = !category && !discountPercentage && !freeShipping && !max && !min && areEqualObjects(sortBy, this.sortOptionDefault);
    this.changedFilters =
      this.filtersApplied.category !== category ||
      this.filtersApplied.discountPercentage !== discountPercentage ||
      this.filtersApplied.freeShipping !== freeShipping ||
      this.filtersApplied.max !== max ||
      this.filtersApplied.min !== min ||
      !areEqualArrays(this.filtersApplied.discounts, discounts) ||
      !areEqualObjects(this.filtersApplied.sortBy, sortBy);
  }
}
